import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';
import { optimisticEntityWrite, type OptimisticCache } from '../optimisticWrite';

export const withTasks = <TBase extends Constructor<any>>(Base: TBase) => class Tasks extends Base {
  // TASKS
  // ============================================================================

  // getTasks()/getTask() re-run enrichment on every call (component re-renders,
  // realtime refreshes, etc.), so an unresolvable reference otherwise logs the
  // same warning dozens of times per session. Cross-workspace references (e.g.
  // an internal-staff or automation-created task in a client project) never
  // resolve for that viewer, so dedupe per task+field+id instead of dropping
  // the warning entirely — a genuinely new bad reference still gets logged once.
  private _taskRefWarningsLogged?: Set<string>;

  private warnMissingTaskRefOnce(key: string, message: string): void {
    if (!this._taskRefWarningsLogged) {
      this._taskRefWarningsLogged = new Set();
    }
    if (this._taskRefWarningsLogged.has(key)) return;
    this._taskRefWarningsLogged.add(key);
    console.warn(message);
  }

  /**
   * Enrich task with related data (user names, project names, etc.)
   * This is needed because backend only stores IDs, not computed fields
   */
  private enrichTask(task: Task): Task {
    // Create a copy to avoid mutating the original
    const enriched = { ...task };

    // Look up created_by_name
    if (enriched.created_by && !enriched.created_by_name) {
      const creator = this.db.users[enriched.created_by];
      if (creator) {
        enriched.created_by_name = creator.name;
      } else {
        this.warnMissingTaskRefOnce(
          `${enriched.id}:creator:${enriched.created_by}`,
          `⚠️ Task ${enriched.id}: Creator user not found: ${enriched.created_by}`,
        );
      }
    }

    // Look up assigned_to_name
    if (enriched.assigned_to && !enriched.assigned_to_name) {
      const assignee = this.db.users[enriched.assigned_to];
      if (assignee) {
        enriched.assigned_to_name = assignee.name;
      } else {
        this.warnMissingTaskRefOnce(
          `${enriched.id}:assignee:${enriched.assigned_to}`,
          `⚠️ Task ${enriched.id}: Assigned user not found: ${enriched.assigned_to}`,
        );
      }
    }

    // Look up project_name, client_company_id, and client_name
    if (enriched.project_id && !enriched.project_name) {
      const project = this.db.projects[enriched.project_id];
      if (project) {
        enriched.project_name = project.name;
        enriched.client_company_id = project.clientId;

        const client = this.db.companies[project.clientId];
        if (client) {
          enriched.client_name = client.name;
        } else {
          this.warnMissingTaskRefOnce(
            `${enriched.id}:client:${project.clientId}`,
            `⚠️ Task ${enriched.id}: Client company not found: ${project.clientId}`,
          );
        }
      } else {
        this.warnMissingTaskRefOnce(
          `${enriched.id}:project:${enriched.project_id}`,
          `⚠️ Task ${enriched.id}: Project not found: ${enriched.project_id}`,
        );
      }
    }

    // Set application-level defaults for fields not in backend schema
    if (enriched.client_can_edit === undefined) {
      enriched.client_can_edit = false;
    }
    if (enriched.visible_to_client === undefined) {
      enriched.visible_to_client = true;
    }
    if (!enriched.notes) {
      enriched.notes = '';
    }
    if (!enriched.source_details) {
      enriched.source_details = '';
    }

    // Map backend completed_at to frontend completed_date if needed. The
    // fetched row can carry either casing depending on the read path
    // (objectToCamelCase drops the snake_case key entirely), so check both.
    const completedAt = ((enriched as any).completed_at ?? (enriched as any).completedAt) as Date | undefined;
    if (completedAt && !enriched.completed_date) {
      enriched.completed_date = completedAt;
    }

    return enriched;
  }

  public getTasks(): Task[] {
    const tasks = (Object.values(this.db.tasks) as Task[]).map((task: Task) => this.enrichTask(task));
    return tasks;
  }

  public getTask(id: string): Task | undefined {
    const task = this.db.tasks[id];
    const enriched = task ? this.enrichTask(task) : undefined;
    return enriched;
  }

  public getTasksByProject(projectId: string): Task[] {
    const tasks = (Object.values(this.db.tasks) as Task[])
      .filter(task => task.project_id === projectId)
      .map(task => this.enrichTask(task));
    return tasks;
  }

  public getTasksByAssignee(userId: string): Task[] {
    return this.getTasks().filter(t => t.assigned_to === userId);
  }

  public getTasksByStatus(status: string): Task[] {
    return this.getTasks().filter(t => t.status === status);
  }

  public async createTask(task: Omit<Task, 'id' | 'created_at' | 'updated_at'>): Promise<Task> {
    const project = this.getProject(task.project_id);
    const client = project ? this.getCompany(project.clientId) : null;
    
    const newTask: Task = {
      ...task,
      id: this.generateId(),
      status: task.status || 'pending', // Ensure status defaults to 'pending'
      project_name: project?.name || '',
      client_company_id: project?.clientId || '',
      client_name: client?.name || '',
      created_at: new Date(),
      updated_at: new Date(),
      client_can_edit: false,
    };
    
    // Save to backend FIRST (primary storage)
    await this.saveEntityToBackend('tasks', newTask.id, newTask, true);
    
    // Then update in-memory state and localStorage (cache only)
    this.db.tasks[newTask.id] = newTask;
    this.saveToLocalStorage();
    
    // Log activity for task creation
    if (project) {
      const creator = this.getUser(task.created_by || '');
      this.logActivity({
        type: 'task_created',
        related_to_type: 'project',
        related_to_id: project.id,
        related_to_name: project.name,
        title: `Task Created: ${newTask.title}`,
        description: `New task "${newTask.title}" created. Priority: ${newTask.priority}. Due: ${newTask.due_date ? new Date(newTask.due_date).toLocaleDateString() : 'Not set'}. Assigned to: ${newTask.assigned_to || 'Unassigned'}`,
        user_id: creator?.id || 'system',
        user_name: creator?.name || 'System',
        user_email: creator?.email || '',
        is_internal: true,
      });
    }
    
    this.notifyListeners();

    // Database triggers atomically enqueue task-created/assigned workflows.

    return newTask;
  }

  public async updateTask(id: string, updates: Partial<Task>): Promise<Task | undefined> {
    const task = this.db.tasks[id];
    if (!task) return undefined;
    
    const updated = {
      ...task,
      ...updates,
      id,
      updated_at: new Date(),
    };
    
    // If project_id changed, update derived fields (project_name, client_company_id, client_name)
    if (updates.project_id && updates.project_id !== task.project_id) {
      const project = this.getProject(updates.project_id);
      const client = project ? this.getCompany(project.clientId) : null;
      
      updated.project_name = project?.name || '';
      updated.client_company_id = project?.clientId || '';
      updated.client_name = client?.name || '';
    }
    
    // Optimistic: lists move now, and a failed save restores the previous row
    // before rethrowing. The PUT carries the whole row, so it is safe to retry.
    await optimisticEntityWrite(this as unknown as OptimisticCache, 'tasks', id, updated, () => this.saveEntityToBackend('tasks', id, updated, false));
    
    // Log activity for significant task changes
    const project = this.getProject(updated.project_id);
    if (project) {
      const updater = this.getUser(updates.assigned_to || task.created_by || '');
      
      // Status change
      if (updates.status && updates.status !== task.status) {
        this.logActivity({
          type: 'status_update',
          related_to_type: 'project',
          related_to_id: project.id,
          related_to_name: project.name,
          title: `Task Status Changed: ${task.title}`,
          description: `Task "${task.title}" status changed from "${task.status}" to "${updates.status}"`,
          user_id: updater?.id || 'system',
          user_name: updater?.name || 'System',
          user_email: updater?.email || '',
          status_from: task.status,
          status_to: updates.status,
          is_internal: true,
        });
      }
      
      // Assignment change
      if (updates.assigned_to && updates.assigned_to !== task.assigned_to) {
        const newAssignee = this.getUser(updates.assigned_to);
        this.logActivity({
          type: 'note',
          related_to_type: 'project',
          related_to_id: project.id,
          related_to_name: project.name,
          title: `Task Reassigned: ${task.title}`,
          description: `Task "${task.title}" assigned to ${newAssignee?.name || 'Unknown'}`,
          user_id: updater?.id || 'system',
          user_name: updater?.name || 'System',
          user_email: updater?.email || '',
          is_internal: true,
        });
      }
      
      // Priority change
      if (updates.priority && updates.priority !== task.priority) {
        this.logActivity({
          type: 'note',
          related_to_type: 'project',
          related_to_id: project.id,
          related_to_name: project.name,
          title: `Task Priority Changed: ${task.title}`,
          description: `Task "${task.title}" priority changed from "${task.priority}" to "${updates.priority}"`,
          user_id: updater?.id || 'system',
          user_name: updater?.name || 'System',
          user_email: updater?.email || '',
          is_internal: true,
        });
      }
    }
    
    this.notifyListeners();

    // Database triggers atomically enqueue assignment/completion workflows.

    return updated;
  }

  public async deleteTask(id: string): Promise<boolean> {
    const task = this.db.tasks[id];
    if (!task) {
      console.error('❌ Task not found:', id);
      return false;
    }
    
    try {
      // Delete from backend FIRST (primary storage)
      await this.deleteEntityFromBackend('tasks', id);
      
      // Log activity for task deletion BEFORE deleting from memory
      const project = this.getProject(task.project_id);
      if (project) {
        this.logActivity({
          type: 'note',
          related_to_type: 'project',
          related_to_id: project.id,
          related_to_name: project.name,
          title: `Task Deleted: ${task.title}`,
          description: `Task "${task.title}" was deleted`,
          user_id: 'system',
          user_name: 'System',
          user_email: '',
          is_internal: true,
        });
      }
      
      // Then delete from in-memory state and localStorage (cache only)
      delete this.db.tasks[id];
      this.saveToLocalStorage();
      
      this.notifyListeners();
      return true;
    } catch (error) {
      console.error('❌ Error in deleteTask:', error);
      // Even if backend fails, we still delete locally
      delete this.db.tasks[id];
      this.saveToLocalStorage();
      this.notifyListeners();
      return true; // Return true because local deletion succeeded
    }
  }

  /**
   * Fix any tasks that are missing the status field
   * Sets them to 'pending' by default
   */
  public fixTasksWithoutStatus(): number {
    let fixedCount = 0;
    Object.keys(this.db.tasks).forEach(taskId => {
      const task = this.db.tasks[taskId];
      if (!task.status) {
        task.status = 'pending';
        task.updated_at = new Date();
        fixedCount++;
      }
    });
    
    if (fixedCount > 0) {
      this.saveToStorage();
      this.notifyListeners();
    }
    
    return fixedCount;
  }

  // ============================================================================

  // TASK COMMENTS
  // ============================================================================

  /**
   * Get all comments for a specific task
   */
  public getTaskComments(taskId: string): TaskComment[] {
    if (!this.db.taskComments) {
      return [];
    }
    return (Object.values(this.db.taskComments) as TaskComment[])
      .filter((comment: TaskComment) => comment.task_id === taskId)
      .sort((a: TaskComment, b: TaskComment) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }

  /**
   * Add a comment to a task
   */
  public async addTaskComment(
    taskId: string,
    userId: string,
    userName: string,
    comment: string,
    visibleToClient: boolean = true
  ): Promise<TaskComment> {
    const newComment: TaskComment = {
      id: this.generateId(),
      task_id: taskId,
      user_id: userId,
      user_name: userName,
      comment,
      created_at: new Date(),
      visible_to_client: visibleToClient,
    };

    // Save to backend FIRST (primary storage)
    await this.saveEntityToBackend('taskComments', newComment.id, newComment, true);

    // Then update in-memory state and localStorage (cache only)
    this.db.taskComments[newComment.id] = newComment;
    this.saveToLocalStorage();

    this.notifyListeners();
    return newComment;
  }

  /**
   * Delete a task comment
   */
  public async deleteTaskComment(commentId: string): Promise<boolean> {
    if (!this.db.taskComments[commentId]) {
      console.error('❌ Comment not found:', commentId);
      return false;
    }

    try {
      // Delete from backend FIRST (primary storage)
      await this.deleteEntityFromBackend('taskComments', commentId);

      // Then delete from in-memory state and localStorage (cache only)
      delete this.db.taskComments[commentId];
      this.saveToLocalStorage();

      this.notifyListeners();
      return true;
    } catch (error) {
      console.error('❌ Error in deleteTaskComment:', error);
      // Even if backend fails, we still delete locally
      delete this.db.taskComments[commentId];
      this.saveToLocalStorage();
      this.notifyListeners();
      return true;
    }
  }

  // ============================================================================
};
